import axios from "axios";
import { toast } from "react-toastify";
import {
  getRateLimitState,
  triggerFreeze,
} from "./RateLimitStore";
import { triggerResExpired } from "./ResExpiredStore";

let tooManyAttemptsToastId = null;

// Marker so the response interceptor below can tell "blocked before send"
// requests apart from real network errors and skip re-triggering the
// freeze/toast for them.
export const RATE_LIMIT_BLOCKED = "RATE_LIMIT_BLOCKED";

// Hard freeze: while the site is frozen (isFrozen === true), don't even send
// new requests to the server — reject them locally so they never leave the
// browser. Existing in-flight requests (sent before the freeze started) are
// left alone and are allowed to finish normally.
axios.interceptors.request.use((config) => {
  if (getRateLimitState().isFrozen) {
    const blockedError = new Error(
      "Request blocked: site is frozen due to rate limiting."
    );
    blockedError.code = RATE_LIMIT_BLOCKED;
    return Promise.reject(blockedError);
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Skip requests we blocked ourselves before they were sent — nothing
    // to freeze/toast for, the site is already frozen.
    if (error?.code === RATE_LIMIT_BLOCKED) {
      return Promise.reject(error);
    }

    if (error?.response?.status === 429) {
      // Freeze the whole website for 30s behind a non-closable overlay
      // (extends the timer if more 429s keep arriving mid-freeze)
      triggerFreeze();

      // Prevent duplicate toasts if multiple requests 429 at the same time
      if (!toast.isActive(tooManyAttemptsToastId)) {
        tooManyAttemptsToastId = toast.error(
          "Too many attempts. Please wait a moment and try again.",
          {
            toastId: "too-many-attempts",
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    }

    // RES_EXPIRED — the backend returns this exact {code, CodeName, Message} shape (HTTP 410)
    // from EVERY migrated Hold/*CombinedFees() endpoint, across every module, whenever the
    // saved FlightSearchResult row behind a ResultId/ResultSetId can no longer be found (older
    // than the 10-min cache freshness window a Hold/fare-family-change depends on, or pruned by
    // the hourly air:prune-flight-search-results cron). Catching it here — once, in the shared
    // interceptor every axios call in the app goes through — means every module gets this popup
    // automatically, with no per-call-site try/catch needed (per explicit instruction: "whenevr
    // this error comes in any module, then show the popup").
    if (error?.response?.data?.code === "RES_EXPIRED") {
      triggerResExpired(error.response.data?.Message);
    }

    return Promise.reject(error);
  }
);
